/**
 * Pause / resume control for the live voice session.
 *
 * Toggles the same `setMicMuted` plumbing the clinical-safety gate
 * uses — flips `track.enabled` on the outbound audio sender and mutes
 * the bot's playback element. WebRTC stays connected, the WS stays
 * open, the LLM stays warm; only the audio frames stop flowing.
 * Resume is instant.
 *
 * Visually mirrors MicButton's glass disc, sized smaller (48px) so
 * it reads as a secondary action sitting alongside the primary mic.
 * Amber accent in paused state matches the existing `isMicMuted`
 * banner above so the two surfaces feel like one mode.
 *
 * Disabled when the session isn't connected — pause/resume only
 * makes sense while audio is actively flowing.
 */

import React from "react";
import { motion } from "framer-motion";

import { tokens } from "./styles";

// Sizes parameterised so the panel can render a compact variant on
// short viewports (≤720px) without losing the visual relationship to
// the primary MicButton. Ratio stays the same; both shrink together.
const SIZE_FULL = 48;
const SIZE_COMPACT = 40;
const INNER_INSET_FULL = 6;
const INNER_INSET_COMPACT = 5;
const ICON_FULL = 18;
const ICON_COMPACT = 14;

const Icon = ({ size = 18, strokeWidth = 1.8, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const PauseIcon = (
  <Icon>
    <rect x="6.5" y="5.5" width="3.5" height="13" rx="1.2" />
    <rect x="14" y="5.5" width="3.5" height="13" rx="1.2" />
  </Icon>
);

const ResumeIcon = (
  <Icon>
    {/* slightly offset so the triangle reads as centered to the eye */}
    <path d="M7.5 5.5 L18 12 L7.5 18.5 Z" />
  </Icon>
);

export function PauseResumeButton({ paused, disabled, onToggle, compact = false }) {
  const label = paused ? "Resume voice session" : "Pause voice session";
  const accent = paused
    ? "rgba(251, 191, 36, 0.55)" // amber — matches the muted banner
    : tokens.accent.cyan;
  const size = compact ? SIZE_COMPACT : SIZE_FULL;
  const innerInset = compact ? INNER_INSET_COMPACT : INNER_INSET_FULL;
  const iconSize = compact ? ICON_COMPACT : ICON_FULL;

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={paused}
      aria-label={label}
      title={label}
      style={{
        position: "relative",
        width: size,
        height: size,
        borderRadius: "50%",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        background: "transparent",
        padding: 0,
        outline: "none",
        opacity: disabled ? 0.35 : 1,
        transition: "opacity 120ms ease",
      }}
    >
      <motion.span
        aria-hidden="true"
        animate={{ scale: paused ? 1.04 : 1, opacity: paused ? 1 : 0.7 }}
        transition={{ type: "spring", stiffness: 240, damping: 20 }}
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          background: `radial-gradient(circle at 30% 30%, ${accent}, transparent 72%)`,
          boxShadow: tokens.shadow.button,
        }}
      />
      <span
        style={{
          position: "absolute",
          inset: innerInset,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          background: paused
            ? "rgba(251, 191, 36, 0.12)"
            : "rgba(15, 23, 42, 0.7)",
          border: `1px solid ${tokens.surface.borderStrong}`,
          color: paused ? "#FCD34D" : tokens.ink[50],
          backdropFilter: "blur(6px)",
        }}
      >
        {React.cloneElement(paused ? ResumeIcon : PauseIcon, { size: iconSize })}
      </span>
    </button>
  );
}
